import { css, StyleSheet } from 'aphrodite';
import React, { ReactNode } from 'react';

import { useHistory, useLocation, useParams } from '@/shared/hooks/use-router/react-router-hooks';

/**
 * Three-pane settings shell — left sidebar with Members / Groups /
 * Permissions / Org tabs, right content area filled by the active
 * route's component. Mounted at /o/:slug/settings/*.
 *
 * The sidebar drives navigation via history.push so deep links to a
 * specific tab work and bookmarks behave.
 */

// Sidebar labels are user-facing; URL slugs (e.g. /settings/groups) stay
// stable so existing links keep working. We render "Roles" because that's
// the IAM-correct word — a group with policies attached IS a role.
const TABS: { id: string; label: string; help: string }[] = [
  { id: 'members', label: 'Members', help: 'People in this org and their access' },
  { id: 'groups', label: 'Roles', help: 'Bundles of permissions you assign to members' },
  { id: 'permissions', label: 'Permissions', help: 'What roles are allowed to do' },
  { id: 'org', label: 'Org', help: 'Name, slug, danger zone' },
];

export default function SettingsLayout({ children }: { children: ReactNode }) {
  const history = useHistory();
  const location = useLocation();
  const { slug } = useParams<{ slug: string }>();

  const active = TABS.find((t) => location.pathname.endsWith(`/${t.id}`))?.id ?? 'members';

  return (
    <div className={css(s.root)}>
      <aside className={css(s.sidebar)}>
        <div className={css(s.sidebarHeader)}>Settings</div>
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={css(s.tab, active === t.id && s.tabActive)}
            onClick={() => history.push(`/o/${slug}/settings/${t.id}`)}
          >
            <span className={css(s.tabLabel)}>{t.label}</span>
            <span className={css(s.tabHelp)}>{t.help}</span>
          </button>
        ))}
        <button
          type="button"
          className={css(s.backLink)}
          onClick={() => history.push(`/o/${slug}/dashboard`)}
        >
          ← Back to dashboard
        </button>
      </aside>
      <main className={css(s.main)}>{children}</main>
    </div>
  );
}

const s = StyleSheet.create({
  root: {
    display: 'flex',
    // Use `height` (not minHeight) so flex children inherit a fixed
    // box. With minHeight the root grows with content, and the right
    // panel's `overflow: auto` never kicks in because nothing is
    // clipping it — the whole page scrolls instead, hiding the
    // sidebar above the fold.
    height: 'calc(100vh - 60px)',
    background: 'var(--td-canvas, #f6f6f6)',
  },
  sidebar: {
    width: 240,
    minWidth: 240,
    padding: '24px 16px',
    background: 'var(--td-white, #fff)',
    borderRight: '1px solid var(--td-gray-100, #f3f3f3)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
  },
  sidebarHeader: {
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--td-gray-500, #777)',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    padding: '0 10px 10px',
  },
  tab: {
    background: 'transparent',
    border: 'none',
    textAlign: 'left' as const,
    padding: '10px 12px',
    borderRadius: 6,
    cursor: 'pointer',
    color: 'var(--td-gray-700, #484848)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    ':hover': { background: 'var(--td-gray-50, #f9f9f9)' },
  },
  tabActive: {
    background: 'var(--td-tile-blue, #2068A8)',
    color: 'var(--td-white, #fff)',
  },
  tabLabel: { fontSize: 13, fontWeight: 600 },
  tabHelp: {
    fontSize: 11,
    opacity: 0.75,
    fontWeight: 400,
    lineHeight: 1.4,
  },
  backLink: {
    marginTop: 'auto',
    background: 'transparent',
    border: 'none',
    color: 'var(--td-gray-500, #777)',
    fontSize: 11,
    textAlign: 'left' as const,
    padding: '12px 10px',
    cursor: 'pointer',
    ':hover': { color: 'var(--td-gray-900, #1a1a1a)' },
  },
  main: {
    flex: 1,
    minWidth: 0, // let flex shrink the child so overflow can clip
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
  },
});
